import { ReactNode, createContext, useState } from 'react'

import { ITask, TStatus } from '@/types/task.interface'

export type DraftState = {
	id: string
	draft: ITask
	controller?: AbortController
}

export type DraftsContextType = {
	drafts: DraftState[]
	addDraft: (draft: ITask) => void
	setController: (id: string, controller?: AbortController) => void
	updateDraftStatus: (id: string, status: TStatus) => void
	removeDraft: (id: string) => void
}

export const DraftsContext = createContext<DraftsContextType>({
	drafts: [],
	addDraft: () => {},
	setController: () => {},
	updateDraftStatus: () => {},
	removeDraft: () => {}
})

export const DraftsProvider = ({ children }: { children: ReactNode }) => {
	const [drafts, setDrafts] = useState<DraftState[]>([])

	const addDraft = (draft: ITask) => {
		setDrafts(prev => {
			if (prev.some(d => d.id === draft.id)) return prev

			return [...prev, { id: draft.id, draft }]
		})
	}

	const setController = (id: string, controller?: AbortController) => {
		setDrafts(prev => prev.map(d => (d.id === id ? { ...d, controller } : d)))
	}

	const updateDraftStatus = (id: string, status: TStatus) => {
		setDrafts(prev =>
			prev.map(d => (d.id === id ? { ...d, draft: { ...d.draft, status } } : d))
		)
	}

	const removeDraft = (id: string) => {
		setDrafts(prev => prev.filter(d => d.id !== id))
	}

	return (
		<DraftsContext.Provider
			value={{
				drafts,
				addDraft,
				setController,
				updateDraftStatus,
				removeDraft
			}}
		>
			{children}
		</DraftsContext.Provider>
	)
}
